"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { formatOrdinal } from "@/helpers/format.helpers";
import { ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { getDropPreviewImageUrl, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Link from "next/link";
interface LeadingCardProps {
  readonly drop: ExtendedDrop;
  readonly rank: number;
}
const getRankLabelClass = (rank: number) => {
  if (rank === 1) return "tw-text-[#E8D48A]";
  if (rank === 2) return "tw-text-[#DDDDDD]";
  if (rank === 3) return "tw-text-[#CD7F32]";
  return "tw-text-white/60";
};
export const LeadingCard = t0 => {
  const $ = _c(57);
  const {
    drop,
    rank
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const mediaImageScale = isTabletOrSmaller ? ImageScale.AUTOx450 : ImageScale.AUTOx600;
  const isHtml = media?.mime_type === "text/html";
  const shouldGate = hasTouchScreen && isHtml;
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  let t1;
  if ($[0] !== rank) {
    t1 = getRankLabelClass(rank);
    $[0] = rank;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rankLabelClass = t1;
  let t2;
  if ($[2] !== drop.metadata || $[3] !== shouldGate) {
    t2 = shouldGate ? getDropPreviewImageUrl(drop.metadata) ?? undefined : undefined;
    $[2] = drop.metadata;
    $[3] = shouldGate;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const htmlPreviewImageUrl = t2;
  let t3;
  bb0: {
    if (!media) {
      let t4;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-iron-950"><span className="tw-text-sm tw-text-white/40">No image</span></div>;
        $[5] = t4;
      } else {
        t4 = $[5];
      }
      t3 = t4;
      break bb0;
    }
    let t4;
    if ($[6] !== htmlPreviewImageUrl || $[7] !== media.mime_type || $[8] !== media.url || $[9] !== mediaImageScale || $[10] !== shouldGate) {
      t4 = <MediaDisplay media_mime_type={media.mime_type} media_url={media.url} disableMediaInteraction={true} imageScale={mediaImageScale} previewImageUrl={htmlPreviewImageUrl} requireInteractionToLoad={shouldGate} />;
      $[6] = htmlPreviewImageUrl;
      $[7] = media.mime_type;
      $[8] = media.url;
      $[9] = mediaImageScale;
      $[10] = shouldGate;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = t4;
  }
  const mediaContent = t3;
  const t4 = `tw-text-[11px] tw-font-semibold tw-leading-5 tw-tracking-wide ${rankLabelClass}`;
  let t5;
  if ($[12] !== rank) {
    t5 = rank === 1 ? "LEADING" : `${formatOrdinal(rank)} PLACE`;
    $[12] = rank;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== drop.rating) {
    t7 = formatNumberWithCommas(drop.rating);
    $[17] = drop.rating;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <span className="tw-text-white/80">{t7}</span>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="tw-text-white/50"> TDH</span>;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8) {
    t10 = <span className="tw-font-mono tw-text-xs">{t8}{t9}</span>;
    $[22] = t8;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t6) {
    t11 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-border-b tw-border-white/10 tw-bg-iron-900 tw-px-2.5 tw-py-1.5 @sm:tw-flex-row @sm:tw-items-center @sm:tw-justify-between sm:tw-px-3 sm:tw-py-2">{t6}{t10}</div>;
    $[24] = t10;
    $[25] = t6;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== mediaContent) {
    t12 = <div className="tw-relative tw-flex tw-aspect-[3/4] tw-max-h-[clamp(320px,70vw,500px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-iron-950"><div className="tw-[&>div]:tw-mx-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-transition-transform tw-duration-700 tw-ease-out group-hover:tw-scale-105">{mediaContent}</div></div>;
    $[27] = mediaContent;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = media?.mime_type;
  let t14;
  if ($[29] !== drop.id || $[30] !== t13) {
    t14 = <MediaTypeBadge mimeType={t13} dropId={drop.id} size="sm" />;
    $[29] = drop.id;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== drop.id || $[33] !== drop.wave.id) {
    t15 = getWaveRoute({
      waveId: drop.wave.id,
      extraParams: {
        drop: drop.id
      },
      isDirectMessage: false,
      isApp: false
    });
    $[32] = drop.id;
    $[33] = drop.wave.id;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15 || $[36] !== title) {
    t16 = <Link href={t15} className="tw-m-0 tw-line-clamp-2 tw-text-base tw-font-semibold tw-leading-tight tw-text-iron-200 tw-no-underline tw-transition-colors group-hover:tw-text-white @lg:tw-line-clamp-1">{title}</Link>;
    $[35] = t15;
    $[36] = title;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t14 || $[39] !== t16) {
    t17 = <div className="tw-flex tw-items-center tw-gap-2">{t14}{t16}</div>;
    $[38] = t14;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const t18 = `/${author.handle ?? author.primary_address}`;
  const t19 = author.handle ?? "Artist";
  let t20;
  if ($[41] !== author.pfp || $[42] !== t19) {
    t20 = <ProfileAvatar pfpUrl={author.pfp} alt={t19} size={ProfileBadgeSize.SMALL} />;
    $[41] = author.pfp;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const t21 = author.handle ?? "Anonymous";
  let t22;
  if ($[44] !== t21) {
    t22 = <span className="tw-min-w-0 tw-truncate tw-text-xs tw-text-white/50 desktop-hover:hover:tw-text-white">{t21}</span>;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t18 || $[47] !== t20 || $[48] !== t22) {
    t23 = <Link href={t18} className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline">{t20}{t22}</Link>;
    $[46] = t18;
    $[47] = t20;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t17 || $[51] !== t23) {
    t24 = <div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-white/10 tw-px-3 tw-py-3 sm:tw-px-4">{t17}{t23}</div>;
    $[50] = t17;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t11 || $[54] !== t12 || $[55] !== t24) {
    t25 = <div className="tw-group tw-flex tw-flex-col tw-text-left tw-transition-all tw-duration-500"><div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-transition-colors group-hover:tw-border-white/10">{t11}{t12}{t24}</div></div>;
    $[53] = t11;
    $[54] = t12;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  return t25;
};
function _temp(m) {
  return m.data_key === "title";
}